import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const NotFoundPage = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let frame = 0;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const particles = Array.from({ length: 38 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.5 + 0.3,
      speed: Math.random() * 0.00015 + 0.00005,
      opacity: Math.random() * 0.18 + 0.04,
      phase: Math.random() * Math.PI * 2,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;
      particles.forEach((p) => {
        const x = ((p.x + frame * p.speed) % 1) * canvas.width;
        const y = p.y * canvas.height;
        const pulse = Math.sin(frame * 0.012 + p.phase) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.arc(x, y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(188, 63, 74, ${p.opacity * pulse})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div className="relative md:pt-44 min-h-screen bg-[#f5f3ea] flex flex-col items-center justify-center px-6 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#BC3F4A]/40 to-transparent" />

      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        aria-hidden="true"
      />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="relative z-10 text-center space-y-6 max-w-md w-full"
      >
        <p className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sourcesans">
          Error
        </p>

        <h1 className="font-playfair font-bold text-[#BC3F4A] text-7xl sm:text-8xl md:text-9xl leading-none">
          404
        </h1>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="w-12 h-px bg-[#BC3F4A]/40 mx-auto"
          style={{ originX: 0.5 }}
        />

        <p className="font-playfair text-xl sm:text-2xl text-stone-700 leading-snug">
          This page doesn't exist{" "}
          <span className="text-[#BC3F4A] italic">anymore.</span>
        </p>

        <p className="text-sm font-sourcesans text-stone-500">
          It may have been moved or removed. Let's get you back on track.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
          <Link
            to="/"
            className="w-full sm:w-auto px-6 py-3 bg-[#BC3F4A] hover:bg-[#a3323b] text-white font-playfair rounded-md transition duration-300 text-sm text-center"
          >
            Take Me Home
          </Link>
          <Link
            to="/blogs"
            className="w-full sm:w-auto px-6 py-3 border border-stone-300 hover:border-[#BC3F4A] text-stone-700 hover:text-[#BC3F4A] font-sourcesans rounded-md transition duration-300 text-sm text-center"
          >
            Read the Blog
          </Link>
        </div>

        <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400 font-sourcesans pt-2">
          Make it. Mean it.
        </p>
      </motion.div>

      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#BC3F4A]/40 to-transparent" />
    </div>
  );
};

export default NotFoundPage;
